export const themes = [
    { name: 'Light', value: 'light' },
    { name: 'Dark', value: 'dark' },
    { name: 'Custom', value: 'custom' },
];
// ── Color manipulation helpers ──────────────────────────────────────
const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const rgbToHex = (r, g, b) => '#' + [r, g, b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
const lighten = (hex, amount) => {
    const [r, g, b] = hexToRgb(hex);
    return rgbToHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
};
// ── Core configurable colors (what admin picks) ─────────────────────
export const coreColorVars = {
    'Primary': '--color-primary',
    'Primary-Dark': '--color-primary-dark',
    'Primary-Light': '--color-primary-light',
    'Secondary': '--color-secondary',
    'Accent': '--color-accent',
    'Background': '--color-background',
    'Foreground': '--color-foreground',
    'Border': '--color-border',
};
export const themeDefaults = {
    light: {
        'Primary': '#495057',
        'Primary-Dark': '#343a40',
        'Primary-Light': '#6c757d',
        'Secondary': '#adb5bd',
        'Accent': '#495057',
        'Background': '#ffffff',
        'Foreground': '#212529',
        'Border': '#dee2e6',
    },
    dark: {
        'Primary': '#adb5bd',
        'Primary-Dark': '#6c757d',
        'Primary-Light': '#ced4da',
        'Secondary': '#868e96',
        'Accent': '#adb5bd',
        'Background': '#1a1a1a',
        'Foreground': '#E3E3E3',
        'Border': '#4A4A4A',
    },
    custom: {
        'Primary': '#495057',
        'Primary-Dark': '#343a40',
        'Primary-Light': '#6c757d',
        'Secondary': '#adb5bd',
        'Accent': '#495057',
        'Background': '#ffffff',
        'Foreground': '#212529',
        'Border': '#dee2e6',
    },
};
// The CSS-variable keys among a community's saved theme settings; the rest are display settings.
export const colorKeys = new Set(Object.values(coreColorVars));
// Core colors from a community's saved theme settings, light defaults filling any gap.
export const coreFromSettings = (settings) => {
    const map = new Map(settings.map(s => [s.key, s.value]));
    const defaults = themeDefaults.light;
    return Object.fromEntries(Object.keys(coreColorVars).map(name => [name, map.get(coreColorVars[name]) || defaults[name]]));
};
export const deriveAllCssVars = (core) => ({
    // Brand
    '--color-primary': core.Primary,
    '--color-primary-dark': core['Primary-Dark'],
    '--color-primary-light': core['Primary-Light'],
    '--color-secondary': core.Secondary,
    '--color-accent': core.Accent,
    // Base
    '--color-foreground': core.Foreground,
    '--color-background': core.Background,
    '--color-surface': lighten(core.Border, 0.6),
    '--color-border': core.Border,
    // Interactive — derived from primary
    '--color-link': core.Primary,
    '--color-link-hover': core['Primary-Dark'],
    '--color-button-bg': core.Primary,
    '--color-button-text': '#ffffff',
    // Semantic — standard, not per-district
    '--color-success': '#198754',
    '--color-warning': '#ffc107',
    '--color-danger': '#dc3545',
    '--color-info': '#0dcaf0',
    // Layout — derived from base
    '--color-header-bg': core.Background,
    '--color-header-text': core.Foreground,
    '--color-card-bg': core.Background,
    '--color-card-border': core.Border,
});
const storageKey = (theme, cssVar) => `${theme}:${cssVar}`;
export const resolveThemeColors = (themeKey) => {
    const defaults = themeDefaults[themeKey] || themeDefaults.light;
    return Object.entries(coreColorVars).map(([name, cssVar]) => {
        const stored = localStorage.getItem(storageKey(themeKey, cssVar));
        return {
            name: name,
            cssVar,
            value: stored || defaults[name],
            default: defaults[name],
            theme: themeKey,
        };
    });
};
const colorsToCore = (colors) => {
    const defaults = themeDefaults.light;
    return Object.fromEntries(Object.keys(coreColorVars).map(name => {
        var _a;
        return [
            name,
            ((_a = colors.find(c => c.name === name)) === null || _a === void 0 ? void 0 : _a.value) || defaults[name],
        ];
    }));
};
export const applyThemeColors = (colors) => {
    const allVars = deriveAllCssVars(colorsToCore(colors));
    const root = document.documentElement;
    Object.entries(allVars).forEach(([k, v]) => root.style.setProperty(k, v, 'important'));
};
export const persistColor = (color) => {
    localStorage.setItem(storageKey(color.theme, color.cssVar), color.value);
};
export const resetThemeColors = (themeKey) => {
    const colors = resolveThemeColors(themeKey).map(c => (Object.assign(Object.assign({}, c), { value: c.default })));
    colors.forEach(persistColor);
    applyThemeColors(colors);
    return colors;
};
